import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { PROFILE_LIST } from '../../config/activityProfileConfig2';

// ─────────────────────────────────────────────────────────────────────────────
// Palette par accentColor
// ─────────────────────────────────────────────────────────────────────────────
const PALETTE = {
  orange:  { rgb: '251,146,60',  text: '#fb923c', glow: 'rgba(251,146,60,0.18)'  },
  sky:     { rgb: '56,189,248',  text: '#38bdf8', glow: 'rgba(56,189,248,0.18)'  },
  purple:  { rgb: '168,85,247',  text: '#a855f7', glow: 'rgba(168,85,247,0.18)'  },
  emerald: { rgb: '52,211,153',  text: '#34d399', glow: 'rgba(52,211,153,0.18)'  },
  amber:   { rgb: '251,191,36',  text: '#fbbf24', glow: 'rgba(251,191,36,0.18)'  },
  fuchsia: { rgb: '232,121,249', text: '#e879f9', glow: 'rgba(232,121,249,0.18)' },
};

// ─────────────────────────────────────────────────────────────────────────────
// Fond : grille + vignette
// ─────────────────────────────────────────────────────────────────────────────
function BackgroundGrid() {
  return (
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(30,41,59,0.9) 0%, #0a0e14 70%)' }} />
      <div className="absolute inset-0 opacity-[0.035]"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.9) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />
      <svg className="absolute inset-0 w-full h-full opacity-[0.025]" xmlns="http://www.w3.org/2000/svg">
        {Array.from({ length: 14 }).map((_, i) => (
          <line key={i}
            x1={`${-10 + i * 8}%`} y1="0%"
            x2={`${10  + i * 8}%`} y2="100%"
            stroke="white" strokeWidth="0.5" />
        ))}
      </svg>
      <div className="absolute inset-0"
        style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.75) 100%)' }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Particules flottantes
// ─────────────────────────────────────────────────────────────────────────────
function BackgroundParticles() {
  const particles = Array.from({ length: 24 }, (_, i) => ({
    id: i,
    x: 5 + Math.random() * 90,
    y: 5 + Math.random() * 90,
    size: Math.random() * 1.6 + 0.4,
    duration: 12 + Math.random() * 14,
    delay: Math.random() * -20,
  }));
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {particles.map(p => (
        <motion.div key={p.id}
          className="absolute rounded-full"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size, background: 'rgba(148,163,184,0.45)' }}
          animate={{ y: [0, -28, 0], opacity: [0, 0.4, 0] }}
          transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: 'easeInOut' }} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ProfileCard — glassmorphisme + tilt 3D
// ─────────────────────────────────────────────────────────────────────────────
function ProfileCard({ profile, index, onSelect }) {
  const ref   = useRef(null);
  const pal   = PALETTE[profile.accentColor] ?? PALETTE.orange;

  const rotX  = useSpring(useMotionValue(0), { stiffness: 220, damping: 32 });
  const rotY  = useSpring(useMotionValue(0), { stiffness: 220, damping: 32 });
  const glowX = useMotionValue(50);
  const glowY = useMotionValue(50);

  const handleMouseMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top)  / rect.height;
    rotY.set((x - 0.5) * 16);
    rotX.set((0.5 - y) * 16);
    glowX.set(x * 100);
    glowY.set(y * 100);
  };

  const handleMouseLeave = () => {
    rotX.set(0); rotY.set(0);
    glowX.set(50); glowY.set(50);
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30, filter: 'blur(8px)' }}
      animate={{ opacity: 1, y: 0,  filter: 'blur(0px)' }}
      transition={{ duration: 0.55, delay: 0.55 + index * 0.07, ease: [0.22, 1, 0.36, 1] }}
      style={{ perspective: 900 }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <motion.button
        style={{ rotateX: rotX, rotateY: rotY, transformStyle: 'preserve-3d' }}
        whileTap={{ scale: 0.97 }}
        onClick={() => onSelect(profile.id)}
        className="group relative w-full text-left outline-none"
      >
        {/* Glassmorphisme */}
        <div className="relative overflow-hidden rounded-2xl border transition-all duration-300"
          style={{
            background:          'rgba(13, 18, 26, 0.75)',
            backdropFilter:      'blur(22px)',
            WebkitBackdropFilter:'blur(22px)',
            borderColor:         `rgba(${pal.rgb}, 0.2)`,
            boxShadow:           '0 1px 0 rgba(255,255,255,0.04) inset',
          }}
        >
          {/* Highlight souris */}
          <motion.div
            className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background: useTransform([glowX, glowY],
                ([x, y]) => `radial-gradient(300px circle at ${x}% ${y}%, rgba(${pal.rgb}, 0.12) 0%, transparent 70%)`),
            }}
          />

          {/* Reflet top */}
          <div className="absolute top-0 left-6 right-6 h-px opacity-30"
            style={{ background: `linear-gradient(90deg, transparent, rgba(${pal.rgb}, 0.7), transparent)` }} />

          {/* Contenu */}
          <div className="relative z-10 p-6 md:p-7 flex flex-col gap-4">

            {/* Icône */}
            <div className="w-11 h-11 rounded-xl flex items-center justify-center text-xl select-none transition-transform duration-300 group-hover:scale-110"
              style={{
                background:  `rgba(${pal.rgb}, 0.1)`,
                border:      `1px solid rgba(${pal.rgb}, 0.18)`,
                boxShadow:   `0 0 18px rgba(${pal.rgb}, 0.1)`,
              }}
            >
              {profile.icon}
            </div>

            {/* Textes */}
            <div className="space-y-1.5">
              <div className="text-[11px] font-black uppercase tracking-tight transition-colors duration-200"
                style={{ color: pal.text }}>
                {profile.label}
              </div>
              {profile.description && (
                <p className="text-[10px] text-zinc-500 leading-relaxed font-sans line-clamp-2">
                  {profile.description}
                </p>
              )}
            </div>

            {/* CTA */}
            <div className="flex items-center justify-between mt-auto pt-3 border-t border-white/[0.04]">
              <span className="text-[9px] uppercase tracking-[0.18em] text-zinc-600 group-hover:text-zinc-400 transition-colors">
                Choisir
              </span>
              <motion.div
                className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: `rgba(${pal.rgb}, 0.1)` }}
                whileHover={{ x: 2 }}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M2 5h6M5.5 2l3 3-3 3"
                    stroke={pal.text} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.button>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ProfileSelectionScreen
// ─────────────────────────────────────────────────────────────────────────────
export default function ProfileSelectionScreen({ onSelect }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center bg-[#0a0e14] overflow-x-hidden"
      style={{ fontFamily: "'JetBrains Mono', 'Fira Code', monospace" }}>

      <BackgroundGrid />
      <BackgroundParticles />

      <div className="relative z-10 w-full max-w-5xl px-5 py-16 flex flex-col items-center">

        {/* ── HEADER ── */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center mb-14"
        >
          {/* Badge statut */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-800/40 bg-emerald-950/20 mb-6"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[9px] text-emerald-500/80 uppercase tracking-[0.2em] font-mono">
              Liaison cœur active
            </span>
          </motion.div>

          {/* Titre */}
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="text-5xl md:text-6xl lg:text-7xl font-black uppercase tracking-tighter text-center leading-none"
            style={{
              background: 'linear-gradient(160deg, #fed7aa 0%, #fb923c 40%, #c2410c 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              filter: 'drop-shadow(0 0 40px rgba(251,146,60,0.22))',
            }}
          >
            Grande École
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, scaleX: 0.5 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="flex items-center gap-3 mt-3"
          >
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-orange-900/60" />
            <span className="text-[11px] md:text-xs tracking-[0.35em] text-zinc-500 uppercase">Contrôle</span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-orange-900/60" />
          </motion.div>
        </motion.div>

        {/* ── SÉPARATEUR ── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="flex items-center gap-5 w-full max-w-2xl mb-10"
        >
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-zinc-800 to-zinc-800/60" />
          <span className="text-[9px] uppercase tracking-[0.25em] text-zinc-600 whitespace-nowrap font-mono">
            Choisissez votre espace de travail
          </span>
          <div className="flex-1 h-px bg-gradient-to-l from-transparent via-zinc-800 to-zinc-800/60" />
        </motion.div>

        {/* ── GRILLE ── */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5 w-full">
          {PROFILE_LIST.map((profile, idx) => (
            <ProfileCard key={profile.id} profile={profile} index={idx} onSelect={onSelect} />
          ))}
        </div>

        {/* ── FOOTER ── */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-14 flex flex-col items-center gap-3"
        >
          <p className="text-[10px] italic text-zinc-700 text-center max-w-sm leading-relaxed font-sans">
            « L'apprentissage ne s'arrête jamais — il change seulement de forme. »
          </p>
          <div className="flex items-center gap-3 text-[9px] uppercase tracking-[0.18em] text-zinc-800 font-mono">
            <span>Grande École</span>
            <span className="w-1 h-1 rounded-full bg-zinc-800" />
            <span>Pilotage 2026</span>
            <span className="w-1 h-1 rounded-full bg-zinc-800" />
            <span>Build 2026.05.23</span>
          </div>
        </motion.div>

      </div>
    </div>
  );
}